import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal, SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import { ArrowLeft, ArrowUpRight, BookOpen, FileText } from "lucide-react";

export default function AuthorDetail() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get(`/authors/${slug}`).then((r) => setAuthor(r.data)).catch(() => setError(true)); }, [slug]);

  if (error) {
    return (
      <div className="container-x section-y" data-testid="author-not-found">
        <SectionEyebrow>Not found</SectionEyebrow>
        <h1 className="mt-6 font-display text-4xl tracking-tight">This profile does not exist.</h1>
        <Link to="/authors" className="mt-6 inline-flex items-center gap-2 text-sm font-medium underline-link"><ArrowLeft className="h-4 w-4" /> All profiles</Link>
      </div>
    );
  }
  if (!author) return <div className="container-x section-y text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid={`author-detail-${author.slug}`}>
      <SEO title={`${author.name} · The Cleanability Institute`} description={author.bio} path={`/authors/${author.slug}`} />

      <section className="container-x pt-16 md:pt-24 pb-12">
        <Link to="/authors" data-testid="back-to-authors" className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All profiles
        </Link>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="aspect-square rounded-3xl border border-border bg-card grid place-items-center relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern opacity-20" />
              <span className="relative font-display text-7xl md:text-8xl font-medium tracking-tight text-[hsl(var(--accent))]">
                {author.name.split(" ").map((s) => s[0]).join("").slice(0, 3)}
              </span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="font-mono-mini text-[hsl(var(--accent))] uppercase">{author.tier} · {author.role}</div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-balance">{author.name}</h1>
            <p className="mt-8 text-base md:text-lg text-foreground/85 leading-relaxed max-w-2xl">{author.bio}</p>
            {author.subjects && (
              <div className="mt-8 flex flex-wrap gap-2">
                {author.subjects.map((s) => (
                  <span key={s} className="text-[10px] font-mono-mini uppercase px-2.5 py-1 rounded-md border border-border text-muted-foreground">{s}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {(author.publications?.length || author.insights?.length) > 0 && (
        <section className="container-x section-y border-t border-border">
          <SectionEyebrow>Body of work</SectionEyebrow>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-[hsl(var(--accent))]" /> Publications ({author.publications?.length || 0})
              </div>
              <ul className="mt-6 space-y-3">
                {(author.publications || []).map((p, i) => (
                  <li key={p.id} data-testid={`author-pub-${i}`} className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-display text-base md:text-lg font-medium tracking-tight">{p.title}</div>
                      <div className="font-mono-mini text-muted-foreground shrink-0">{p.year}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{p.type}</div>
                  </li>
                ))}
                {(!author.publications || author.publications.length === 0) && <li className="text-sm text-muted-foreground">None on record.</li>}
              </ul>
            </div>
            <div>
              <div className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
                <FileText className="h-4 w-4 text-[hsl(var(--accent))]" /> Essays ({author.insights?.length || 0})
              </div>
              <ul className="mt-6 space-y-3">
                {(author.insights || []).map((p, i) => (
                  <li key={p.id} data-testid={`author-insight-${i}`}>
                    <Link to={`/insights/${p.slug}`} className="block rounded-xl border border-border bg-card p-5 hover-lift">
                      <div className="font-display text-base md:text-lg font-medium tracking-tight">{p.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{p.category} · {p.published_at}</div>
                    </Link>
                  </li>
                ))}
                {(!author.insights || author.insights.length === 0) && <li className="text-sm text-muted-foreground">None on record.</li>}
              </ul>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
